import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, ChevronLeft, Trash01, Tag01, CreditCard01 } from "@opendate-app/setlist";
import SeatPill from "../../../shared/SeatPill";
import "../../TeamManager/styles/teamManagerStyles.css";
import "../styles/resaleListings.css";
import { delistListing } from "../../../shared/api/resaleListings";
import { formatCents, buildSeatPills } from "../../../shared/formattingUtils";
import EditPriceDialog from "./EditPriceDialog";

const STATE_LABELS = {
  active: "Active",
  delisted: "Delisted",
  canceled_event: "Event canceled",
};
const STATE_PILL_CLASSES = {
  active: "resale-pill--state-active",
  delisted: "resale-pill--state-delisted",
  canceled_event: "resale-pill--state-canceled",
};

const stateLabel = (listing) => {
  if (listing.state === "canceled_event") return listing.calendar_event?.canceled === false ? "Event postponed" : "Event canceled";
  if (listing.sold_out) return "Sold";
  return STATE_LABELS[listing.state] || STATE_LABELS.active;
};
const stateClass = (state) => STATE_PILL_CLASSES[state] || STATE_PILL_CLASSES.active;

const formatListedOn = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const initialsFromEvent = (name) =>
  String(name || "L")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "L";

const formatListingId = (eventName, id) => `#${initialsFromEvent(eventName)}-${id}`;

const seatTags = (items = []) => [
  ...buildSeatPills(items),
  items.length === 1 ? "1 Ticket" : `${items.length} Tickets`,
];

const ListingSummaryCard = ({ listing }) => {
  const items = listing.items || [];
  const pricing = listing.pricing_summary || {};
  const ticketCount = items.length || 1;

  const listPriceCents = pricing.list_price_cents ?? listing.list_price_cents;
  const sellerFeeTotal = pricing.seller_side_fees_cents ?? 0;
  const sellerFeePerTicket = Math.round(sellerFeeTotal / ticketCount);
  // Zero unless the ticket type absorbs, in which case it is the slice of the list
  // price the buyer's fees eat. Without its own row, listing price minus listing fee
  // would not equal "You receive" (O-5221).
  const absorbedFeePerTicket = Math.round(
    (pricing.absorbed_buyer_fees_cents ?? 0) / ticketCount,
  );
  const projectedNetTotal = pricing.projected_seller_net_cents;
  const projectedNetPerTicket =
    projectedNetTotal != null ? Math.round(projectedNetTotal / ticketCount) : null;

  return (
    <section className="listing-show-card">
      <h2 className="listing-show-card-title">Listing Summary</h2>

      <div className="listing-show-row">
        <span className="listing-show-row-label">Seats</span>
        <div className="listing-show-row-tags">
          {seatTags(items).map((t) => (
            <SeatPill key={t}>{t}</SeatPill>
          ))}
        </div>
      </div>

      <div className="listing-show-row">
        <span className="listing-show-row-label">Listing price</span>
        <span className="listing-show-row-value">
          <span className="listing-show-row-value-strong">{formatCents(listPriceCents)}</span>
          <span className="listing-show-row-suffix">per ticket</span>
        </span>
      </div>

      <div className="listing-show-row">
        <span className="listing-show-row-label">
          Service fee ({listPriceCents > 0 ? Math.round((sellerFeePerTicket / listPriceCents) * 100) : 0}%)
        </span>
        <span className="listing-show-row-value">
          {formatCents(sellerFeePerTicket)}
          <span className="listing-show-row-suffix">per ticket</span>
        </span>
      </div>

      {absorbedFeePerTicket > 0 && (
        <div className="listing-show-row">
          <span className="listing-show-row-label">Buyer fees (included in your price)</span>
          <span className="listing-show-row-value">
            {formatCents(absorbedFeePerTicket)}
            <span className="listing-show-row-suffix">per ticket</span>
          </span>
        </div>
      )}

      {projectedNetPerTicket != null && (
        <div className="listing-show-row listing-show-row--receive">
          <span className="listing-show-row-label">You receive</span>
          <span className="listing-show-row-value">
            {formatCents(projectedNetPerTicket)}
            <span className="listing-show-row-suffix">per ticket</span>
          </span>
        </div>
      )}
    </section>
  );
};

const daysActive = (createdAt) => {
  if (!createdAt) return 1;
  const ms = Date.now() - new Date(createdAt).getTime();
  return Math.max(1, Math.round(ms / 86400000));
};

const ListingActivityCard = ({ listing }) => {
  return (
    <section className="listing-show-card">
      <h2 className="listing-show-card-title">Listing Activity</h2>
      <div className="listing-activity-stats" style={{ display: "block" }}>
        <div className="listing-activity-stat" style={{ width: "100%" }}>
          <div className="listing-activity-num">{daysActive(listing.created_at)}</div>
          <div className="listing-activity-label">Days Active</div>
        </div>
      </div>
    </section>
  );
};

const PayoutInfoCard = ({ payoutsReady, payoutsStateBlocked }) => {
  return (
    <section className="listing-show-card">
      <h2 className="listing-show-card-title">Payout Info</h2>
      <div className="listing-show-row">
        <span className="listing-show-row-label">Payout method</span>
        <span className="listing-show-row-value listing-show-row-value-icon">
          <CreditCard01 width={16} height={16} aria-hidden="true" />
          {payoutsStateBlocked ? (
            "Payouts aren't available in your state"
          ) : payoutsReady ? (
            "Bank account connected"
          ) : (
            <a href="/settings/payout">Set up payouts</a>
          )}
        </span>
      </div>
      <div className="listing-show-row">
        <span className="listing-show-row-label">Payment timeline</span>
        <span className="listing-show-row-value">10 days after event</span>
      </div>
    </section>
  );
};

const ListingDetail = ({ listing, onChange, payoutsReady, payoutsStateBlocked }) => {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const event = listing.calendar_event || {};
  const listingId = useMemo(() => formatListingId(event.name, listing.id), [event.name, listing.id]);
  const listedOn = formatListedOn(listing.created_at);
  const isActive = listing.state === "active" && !listing.sold_out;
  const anySold = (listing.items_sold_count || 0) > 0;

  const handleDelist = async () => {
    const ok = window.confirm("Remove this listing? Available items will be withdrawn.");
    if (!ok) return;
    setBusy(true);
    try {
      const updated = await delistListing({ id: listing.id });
      if (updated && updated.id) {
        onChange(updated);
      } else {
        onChange({ ...listing, state: "delisted" });
      }
    } catch (_e) {
      // toast handled in API layer
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="listing-show">
      <div className="listing-show-header">
        <div className="listing-show-header-text">
          <a href="/resale_listings" className="listing-show-back">
            <ChevronLeft width={16} height={16} aria-hidden="true" /> My Listings
          </a>
          <div className="listing-show-title-row">
            <h1 className="listing-show-title">
              Your {event.name ? `${event.name} ` : ""}Listing
            </h1>
            <span className={`resale-pill ${stateClass(listing.state)} resale-pill-with-dot`}>
              <span className="resale-pill-dot" aria-hidden="true" />
              {stateLabel(listing)}
            </span>
          </div>
          <div className="listing-show-meta">
            {listedOn && <>Listed on {listedOn}</>}
            {listedOn && " · "}
            Listing ID: {listingId}
          </div>
        </div>
        {isActive && (
          <div className="listing-show-actions">
            <Button
              variant="secondary"
              icon={Trash01}
              onClick={handleDelist}
              disabled={busy}
              style={{ color: "#B42318", borderColor: "#FECDCA" }}
            >
              {busy ? "Removing..." : "Remove Listing"}
            </Button>
            {!anySold && (
              <Button variant="primary" icon={Tag01} onClick={() => setEditing(true)} disabled={busy}>
                Edit Price
              </Button>
            )}
          </div>
        )}
      </div>

      <ListingSummaryCard listing={listing} />
      <ListingActivityCard listing={listing} />
      <PayoutInfoCard payoutsReady={payoutsReady} payoutsStateBlocked={payoutsStateBlocked} />

      {editing && (
        <EditPriceDialog listing={listing} onClose={() => setEditing(false)} onSaved={onChange} />
      )}
    </div>
  );
};

const listingShape = PropTypes.shape({
  id: PropTypes.number,
  state: PropTypes.string,
  sold_out: PropTypes.bool,
  items_sold_count: PropTypes.number,
  list_price_cents: PropTypes.number,
  created_at: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object),
  pricing_summary: PropTypes.object,
  calendar_event: PropTypes.object,
});

ListingSummaryCard.propTypes = {
  listing: listingShape.isRequired,
};

ListingActivityCard.propTypes = {
  listing: listingShape.isRequired,
};

PayoutInfoCard.propTypes = {
  payoutsReady: PropTypes.bool,
  payoutsStateBlocked: PropTypes.bool,
};

PayoutInfoCard.defaultProps = {
  payoutsReady: false,
  payoutsStateBlocked: false,
};

ListingDetail.propTypes = {
  listing: listingShape.isRequired,
  onChange: PropTypes.func.isRequired,
  payoutsReady: PropTypes.bool,
  payoutsStateBlocked: PropTypes.bool,
};

ListingDetail.defaultProps = {
  payoutsReady: false,
  payoutsStateBlocked: false,
};

export default ListingDetail;
